import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Bell, Share2, Maximize2, Plus, ChevronLeft,
  Music2, Link2, GripVertical, Rocket, Check, Users, Search,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import SongPickerDialog from '../components/SongPickerDialog';
import SongDetailPanel from '../components/SongDetailPanel';
import SongViewerModal from '../components/SongViewerModal';
import { lineupsAPI, songsAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { toast } from 'sonner';
import { useModalHistory } from '../hooks/useModalHistory';

const formatServiceDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const AUTOSAVE_DELAY = 1200;

export default function LineupDetail() {
  const { lineupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [lineup, setLineup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nameEdit, setNameEdit] = useState('');
  const [editingName, setEditingName] = useState(false);

  // Songs
  const [allSongs, setAllSongs] = useState([]);
  const [contents, setContents] = useState({});
  const [loadingContent, setLoadingContent] = useState({});
  const [selectedSongIdx, setSelectedSongIdx] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Share dialog
  const [shareOpen, setShareOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedShareIds, setSelectedShareIds] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  // Notify
  const [notifying, setNotifying] = useState(false);

  // Promote dialog
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [eligibleServices, setEligibleServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [promoting, setPromoting] = useState(false);

  // Autosave ref
  const autosaveTimer = useRef(null);
  const localItemsRef = useRef(null);
  const localNameRef = useRef(null);
  // Real-time sync refs (mirrors ServiceDetail pattern)
  const savedDataRef = useRef(null);   // JSON of last saved {name, items} — baseline for conflict detection

  // ── Back-button closes dialogs / song-detail panel ───────────────────────
  const pickerModal = useModalHistory(pickerOpen, setPickerOpen);
  const shareModal = useModalHistory(shareOpen, setShareOpen);
  const promoteModal = useModalHistory(promoteOpen, setPromoteOpen);

  // selectedSongIdx is a number|null, managed manually
  const selectedSongIdxRef = useRef(selectedSongIdx);
  useEffect(() => { selectedSongIdxRef.current = selectedSongIdx; }, [selectedSongIdx]);
  const songDetailHistoryPushedRef = useRef(false);
  const songDetailSuppressRef = useRef(false);
  useEffect(() => {
    const handler = () => {
      if (songDetailSuppressRef.current) { songDetailSuppressRef.current = false; return; }
      if (selectedSongIdxRef.current !== null) {
        setSelectedSongIdx(null);
        songDetailHistoryPushedRef.current = false;
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const openSongDetail = useCallback((idx) => {
    window.history.pushState({ lineupDetail: 'song' }, '');
    songDetailHistoryPushedRef.current = true;
    setSelectedSongIdx(idx);
  }, []);

  const closeSongDetail = useCallback(() => {
    setSelectedSongIdx(null);
    if (songDetailHistoryPushedRef.current) {
      songDetailSuppressRef.current = true;
      songDetailHistoryPushedRef.current = false;
      window.history.back();
    }
  }, []);
  const lineupRef = useRef(null);      // always-current lineup state (avoids stale closures)
  const viewerOpenRef = useRef(false); // skip sync while fullscreen is open
  useEffect(() => { lineupRef.current = lineup; }, [lineup]);
  useEffect(() => { viewerOpenRef.current = viewerOpen; }, [viewerOpen]);

  const isOwner = lineup?.created_by === user?.user_id;
  const hasEditPermission = !!(lineup && (
    lineup.created_by === user?.user_id ||
    (lineup.shared_with || []).some(sw => sw.user_id === user?.user_id)
  ));

  // Song meta lookup
  const songMap = useMemo(() => {
    const m = {};
    for (const s of allSongs) m[s.song_id] = s;
    return m;
  }, [allSongs]);

  const getSongMeta = useCallback((id) => songMap[id] || null, [songMap]);

  const lineupSongs = useMemo(() => {
    if (!lineup) return [];
    return (lineup.items || []).map((item, idx) => {
      const meta = getSongMeta(item.song_id) || {};
      return {
        ...meta,
        song_id: item.song_id,
        key: item.key !== undefined ? item.key : (meta.key || ''),
        bpm: item.tempo != null && item.tempo !== '' ? item.tempo : (meta.bpm || ''),
        originalBpm: meta.bpm || '',
        accidental: item.accidental || null,
        links: item.links || [],
        attachments: [],
        _idx: idx,
      };
    }).filter(s => s.title);
  }, [lineup, getSongMeta]);

  // Save items to server with debounce
  const scheduleSave = useCallback((newName, newItems) => {
    localNameRef.current = newName;
    localItemsRef.current = newItems;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await lineupsAPI.update(lineupId, localNameRef.current, localItemsRef.current);
        // Update baseline so the Firestore listener knows this is the committed state
        savedDataRef.current = JSON.stringify({ name: localNameRef.current, items: localItemsRef.current });
      } catch {
        toast.error('Auto-save failed');
      } finally {
        setSaving(false);
      }
    }, AUTOSAVE_DELAY);
  }, [lineupId]);

  // Update items locally and trigger save
  const updateItems = useCallback((newItems) => {
    setLineup(prev => {
      if (!prev) return prev;
      const updated = { ...prev, items: newItems };
      scheduleSave(prev.name, newItems);
      return updated;
    });
  }, [scheduleSave]);

  // Load lineup + only the songs currently in the lineup (not all 2000+ songs)
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const lineupRes = await lineupsAPI.getById(lineupId);
        const data = lineupRes.data;
        setLineup(data);
        setNameEdit(data.name || '');

        // Fetch metadata only for songs already in this lineup — avoids loading
        // the entire song library on every page open (major Firestore cost saving).
        const songIds = (data.items || []).map(s => s.song_id).filter(Boolean);
        if (songIds.length > 0) {
          const songsRes = await songsAPI.getPickerByIds(songIds);
          setAllSongs(songsRes.data || []);
        }

        // Initialise baseline for real-time merge conflict detection
        savedDataRef.current = JSON.stringify({ name: data.name, items: data.items || [] });
      } catch (err) {
        toast.error('Failed to load lineup');
        navigate('/lineups');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => clearTimeout(autosaveTimer.current);
  }, [lineupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time collaborative sync via Firestore onSnapshot.
  // Mirrors ServiceDetail's per-field merge logic:
  //   - Only apply incoming field if the local state has no unsaved edit on that field.
  //   - Always apply shared_with / participant_ids (owner-only; collaborators just observe).
  //   - Skip all updates while the fullscreen viewer is open.
  useEffect(() => {
    if (!lineupId || !firestoreDb) return;
    let unsubscribe;
    try {
      const q = query(collection(firestoreDb, 'lineups'), where('lineup_id', '==', lineupId));
      unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.empty || !savedDataRef.current) return;
        if (viewerOpenRef.current) return; // never disrupt fullscreen viewing
        const incoming = snapshot.docs[0].data();
        const saved = JSON.parse(savedDataRef.current);
        const current = lineupRef.current;
        if (!current) return;

        const updates = {};

        // name — skip if the viewer has an unsaved local rename in progress
        if (incoming.name !== undefined) {
          const hasLocalEdit = JSON.stringify(current.name) !== JSON.stringify(saved.name);
          if (!hasLocalEdit && incoming.name !== current.name) updates.name = incoming.name;
        }

        // items — skip if the viewer has pending unsaved song changes
        if (incoming.items !== undefined) {
          const hasLocalEdit = JSON.stringify(current.items) !== JSON.stringify(saved.items);
          const differs = JSON.stringify(incoming.items) !== JSON.stringify(current.items);
          if (!hasLocalEdit && differs) updates.items = incoming.items;
        }

        // shared_with & participant_ids — always sync (owner-only writes)
        if (JSON.stringify(incoming.shared_with) !== JSON.stringify(current.shared_with)) {
          updates.shared_with = incoming.shared_with || [];
        }
        if (JSON.stringify(incoming.participant_ids) !== JSON.stringify(current.participant_ids)) {
          updates.participant_ids = incoming.participant_ids || [];
        }

        if (Object.keys(updates).length === 0) return;

        // Advance the saved baseline for synced fields so subsequent edits
        // by this user are correctly diffed against the new remote state.
        if (updates.name !== undefined || updates.items !== undefined) {
          savedDataRef.current = JSON.stringify({
            name: updates.name !== undefined ? updates.name : saved.name,
            items: updates.items !== undefined ? updates.items : saved.items,
          });
        }

        setLineup(prev => prev ? { ...prev, ...updates } : prev);
        if (updates.name !== undefined) setNameEdit(updates.name);
      }, () => { /* Firestore unavailable — silent fail */ });
    } catch { /* Firestore not configured */ }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [lineupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Song operations ────────────────────────────────────────────
  const addSong = useCallback((songOrId) => {
    const isObj = typeof songOrId === 'object' && songOrId !== null;
    const songId = isObj ? songOrId.song_id : songOrId;
    const meta = isObj ? songOrId : (songMap[songId] || {});
    // Cache the metadata for the newly-added song so it appears in the lineup list
    if (!songMap[songId]) {
      setAllSongs(prev => [...prev, { song_id: songId, ...meta }]);
    }
    const newItem = { song_id: songId, key: meta.key || '', tempo: meta.bpm || '', accidental: null, links: [] };
    updateItems([...(lineup?.items || []), newItem]);
    pickerModal.close();
  }, [lineup, songMap, updateItems, pickerModal]);

  const removeSong = useCallback((songId) => {
    updateItems((lineup?.items || []).filter(s => s.song_id !== songId));
    closeSongDetail();
  }, [lineup, updateItems, closeSongDetail]);

  const updateItemField = useCallback((songId, field, value) => {
    updateItems((lineup?.items || []).map(s =>
      s.song_id === songId ? { ...s, [field]: value } : s
    ));
  }, [lineup, updateItems]);

  const addLink = useCallback((songId, link) => {
    updateItems((lineup?.items || []).map(s =>
      s.song_id === songId ? { ...s, links: [...(s.links || []), link] } : s
    ));
  }, [lineup, updateItems]);

  const removeLink = useCallback((songId, linkIdx) => {
    updateItems((lineup?.items || []).map(s =>
      s.song_id === songId ? { ...s, links: (s.links || []).filter((_, i) => i !== linkIdx) } : s
    ));
  }, [lineup, updateItems]);

  // ── DnD ───────────────────────────────────────────────────────
  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const items = [...(lineup?.items || [])];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    updateItems(items);
  }, [lineup, updateItems]);

  // ── Content fetching ──────────────────────────────────────────
  const fetchContent = useCallback(async (songId) => {
    if (contents[songId] !== undefined || loadingContent[songId]) return;
    setLoadingContent(prev => ({ ...prev, [songId]: true }));
    try {
      const res = await songsAPI.getById(songId);
      const content = res.data.content || '';
      setContents(prev => ({ ...prev, [songId]: content }));
      try { localStorage.setItem(`song_content_${songId}`, content); } catch { /* quota */ }
    } catch {
      try {
        const cached = localStorage.getItem(`song_content_${songId}`);
        setContents(prev => ({ ...prev, [songId]: cached || '' }));
      } catch {
        setContents(prev => ({ ...prev, [songId]: '' }));
      }
    } finally {
      setLoadingContent(prev => ({ ...prev, [songId]: false }));
    }
  }, [contents, loadingContent]);

  const handleOpenViewer = (idx = 0) => {
    setContents({});
    setLoadingContent({});
    setSelectedSongIdx(null);
    setViewerIndex(idx);
    window.history.pushState({ viewer: 'song' }, '');
    setViewerOpen(true);
  };

  // ── Name editing ──────────────────────────────────────────────
  const handleNameBlur = async () => {
    setEditingName(false);
    const trimmed = nameEdit.trim() || 'Untitled Lineup';
    if (trimmed === lineup?.name) return;
    setLineup(prev => prev ? { ...prev, name: trimmed } : prev);
    scheduleSave(trimmed, lineup?.items || []);
  };

  // ── Share ─────────────────────────────────────────────────────
  const openShare = async () => {
    shareModal.open();
    setMemberSearch('');
    setSelectedShareIds((lineup?.shared_with || []).map(sw => sw.user_id));
    setLoadingMembers(true);
    try {
      const res = await lineupsAPI.getShareableUsers({ q: '', limit: 100 });
      setMembers(res.data.users || []);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Debounced server-side member search — re-fetches whenever the search text changes
  useEffect(() => {
    if (!shareOpen || memberSearch === '') return;
    const timer = setTimeout(async () => {
      setLoadingMembers(true);
      try {
        const res = await lineupsAPI.getShareableUsers({ q: memberSearch, limit: 100 });
        setMembers(res.data.users || []);
      } catch { /* silent */ } finally {
        setLoadingMembers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, shareOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await lineupsAPI.share(lineupId, selectedShareIds);
      setLineup(res.data);
      shareModal.close();
      toast.success('Lineup sharing updated');
    } catch {
      toast.error('Failed to update sharing');
    } finally {
      setSharing(false);
    }
  };

  // ── Notify ────────────────────────────────────────────────────
  const handleNotify = async () => {
    setNotifying(true);
    try {
      await lineupsAPI.notify(lineupId);
      toast.success('Shared users notified');
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  // ── Promote ───────────────────────────────────────────────────
  const openPromote = async () => {
    promoteModal.open();
    setSelectedServiceId('');
    setServiceSearch('');
    setLoadingServices(true);
    try {
      const res = await lineupsAPI.getEligibleServices({ q: '', limit: 50 });
      setEligibleServices(res.data.services || []);
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoadingServices(false);
    }
  };

  // Debounced server-side service search in the promote dialog
  useEffect(() => {
    if (!promoteOpen || serviceSearch === '') return;
    const timer = setTimeout(async () => {
      setLoadingServices(true);
      try {
        const res = await lineupsAPI.getEligibleServices({ q: serviceSearch, limit: 50 });
        setEligibleServices(res.data.services || []);
      } catch { /* silent */ } finally {
        setLoadingServices(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [serviceSearch, promoteOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromote = async () => {
    if (!selectedServiceId) return;
    setPromoting(true);
    try {
      await lineupsAPI.promote(lineupId, selectedServiceId);
      toast.success('Lineup promoted to service setlist!');
      navigate('/lineups');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to promote lineup');
    } finally {
      setPromoting(false);
    }
  };

  const selectedSong = selectedSongIdx !== null ? lineupSongs[selectedSongIdx] : null;
  const empty = lineupSongs.length === 0;

  if (loading) {
    return (
      <div className="bg-slate-50 dark:bg-slate-950 flex items-center justify-center min-h-dvh">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lineup) return null;

  return (
    <div className="bg-slate-50 dark:bg-slate-950">
      <Navbar />

      {/* ── Page header ── */}
      <div className="sticky top-14 sm:top-16 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-700/60">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Back */}
            <button
              data-testid="lineup-back-btn"
              onClick={() => navigate('/lineups')}
              className="shrink-0 p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {/* Editable name */}
            <div className="flex-1 min-w-0">
              {editingName ? (
                <input
                  data-testid="lineup-name-edit-input"
                  type="text"
                  value={nameEdit}
                  onChange={e => setNameEdit(e.target.value)}
                  onBlur={handleNameBlur}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  autoFocus
                  className="w-full text-base sm:text-lg font-bold bg-transparent border-b-2 border-green-500 outline-none text-slate-900 dark:text-white py-0.5 text-[16px]"
                  style={{ fontSize: '16px' }}
                />
              ) : (
                <button
                  data-testid="lineup-name-display"
                  onClick={() => hasEditPermission && setEditingName(true)}
                  className={`text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate block max-w-full text-left leading-tight ${hasEditPermission ? 'hover:text-green-700 dark:hover:text-green-400 transition-colors' : ''}`}
                  title={hasEditPermission ? 'Click to rename' : undefined}
                >
                  {lineup.name}
                </button>
              )}
              <div className="flex items-center gap-2 mt-0.5">
                {saving && <span className="text-xs text-slate-400 animate-pulse">Saving…</span>}
                {!saving && !isOwner && (
                  <span className="text-xs text-slate-400 truncate">by {lineup.owner_name}</span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
              {(lineup.shared_with || []).length > 0 && (
                <button
                  data-testid="lineup-notify-btn"
                  onClick={handleNotify}
                  disabled={notifying}
                  title="Notify shared users"
                  className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {notifying
                    ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : <Bell className="w-3.5 h-3.5" />
                  }
                </button>
              )}
              {isOwner && (
                <button
                  data-testid="lineup-share-btn"
                  onClick={openShare}
                  title="Share lineup"
                  className="relative flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  {(lineup.shared_with || []).length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-600 text-white text-[9px] font-bold flex items-center justify-center">
                      {(lineup.shared_with || []).length}
                    </span>
                  )}
                </button>
              )}
              {isOwner && (
                <button
                  data-testid="lineup-promote-btn"
                  onClick={openPromote}
                  title="Promote to service"
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-green-700 text-white hover:bg-green-800 transition-colors"
                >
                  <Rocket className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-3xl mx-auto px-4 pt-5 pb-24">
        {/* Setlist header row */}
        <div className="flex items-center justify-between mb-4 gap-2">
          {selectedSong ? (
            <button
              onClick={() => closeSongDetail()}
              data-testid="lineup-song-detail-back-btn"
              className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Lineup
            </button>
          ) : (
            <p className="text-sm text-slate-400">
              {empty ? 'No songs yet' : `${lineupSongs.length} song${lineupSongs.length !== 1 ? 's' : ''}`}
            </p>
          )}
          {!selectedSong && (
            <div className="flex items-center gap-2">
              {!empty && (
                <button
                  data-testid="lineup-fullscreen-btn"
                  onClick={() => handleOpenViewer(0)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-white text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <Maximize2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden sm:inline">Open Fullscreen</span>
                </button>
              )}
              {hasEditPermission && (
                <button
                  data-testid="lineup-add-song-btn"
                  onClick={() => pickerModal.open()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-700 text-white text-sm font-medium rounded-lg hover:bg-green-800 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 shrink-0" />
                  Add Song
                </button>
              )}
            </div>
          )}
        </div>

        {/* Song detail panel (replaces list) */}
        {selectedSong ? (
          <SongDetailPanel
            serviceId={null}
            song={selectedSong}
            programItems={[]}
            hasEditPermission={hasEditPermission}
            onClose={() => closeSongDetail()}
            onUpdateKey={(key) => updateItemField(selectedSong.song_id, 'key', key)}
            onUpdateTempo={(tempo) => updateItemField(selectedSong.song_id, 'tempo', tempo)}
            onUpdateAccidental={(a) => updateItemField(selectedSong.song_id, 'accidental', a)}
            onAddLink={(link) => addLink(selectedSong.song_id, link)}
            onRemoveLink={(linkIdx) => removeLink(selectedSong.song_id, linkIdx)}
            onOpenViewer={() => handleOpenViewer(selectedSongIdx)}
            onRemove={() => removeSong(selectedSong.song_id)}
            contents={contents}
            loadingContent={loadingContent}
            onFetchContent={fetchContent}
          />
        ) : empty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="lineup-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Music2 className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No songs in this lineup yet</p>
            {hasEditPermission && (
              <button
                data-testid="lineup-add-song-empty-btn"
                onClick={() => setPickerOpen(true)}
                className="mt-3 text-sm text-green-600 hover:text-green-800 font-medium"
              >
                Add songs to get started
              </button>
            )}
          </div>
        ) : (
          /* ── DnD Song List ── */
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="lineup-songs">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden divide-y divide-slate-200 dark:divide-white/10"
                  data-testid="lineup-songs-list"
                >
                  {lineupSongs.map((song, idx) => (
                    <Draggable
                      key={song.song_id}
                      draggableId={song.song_id}
                      index={idx}
                      isDragDisabled={!hasEditPermission}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          data-testid={`lineup-song-${song.song_id}`}
                          className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3.5 transition-colors ${
                            dragSnapshot.isDragging
                              ? 'bg-green-50 dark:bg-green-500/10 shadow-lg'
                              : 'bg-white dark:bg-slate-800/80 hover:bg-slate-50 dark:hover:bg-white/5'
                          }`}
                        >
                          {/* Drag handle */}
                          {hasEditPermission && (
                            <div
                              {...dragProvided.dragHandleProps}
                              className="shrink-0 p-1 text-slate-300 dark:text-slate-600 hover:text-slate-500 dark:hover:text-slate-400 cursor-grab active:cursor-grabbing touch-none"
                              data-testid={`lineup-drag-handle-${song.song_id}`}
                            >
                              <GripVertical className="w-4 h-4" />
                            </div>
                          )}
                          <span className="shrink-0 w-6 h-6 rounded-full bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-xs font-semibold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <button
                            onClick={() => openSongDetail(idx)}
                            data-testid={`lineup-song-btn-${song.song_id}`}
                            className="flex-1 min-w-0 text-left py-0.5"
                          >
                            <p className="text-sm font-medium text-slate-900 dark:text-white break-words leading-snug">{song.title}</p>
                            {song.artist && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 break-words">{song.artist}</p>
                            )}
                            {(song.key || song.bpm || song.links?.length > 0) && (
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                {song.key && (
                                  <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 text-xs font-mono font-semibold rounded">
                                    {song.key}
                                  </span>
                                )}
                                {song.bpm && (
                                  <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 text-xs font-mono rounded">
                                    {song.bpm} BPM
                                  </span>
                                )}
                                {song.links?.length > 0 && (
                                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 text-slate-400 dark:text-slate-500 text-xs rounded">
                                    <Link2 className="w-2.5 h-2.5" />
                                    {song.links.length}
                                  </span>
                                )}
                              </div>
                            )}
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* ── Song Picker (server-side search) ── */}
      <SongPickerDialog
        open={pickerOpen}
        onOpenChange={pickerModal.onOpenChange}
        serverSideSearch={true}
        onServerSearch={async (q, lang, cursor) => {
          const res = await songsAPI.searchPicker({
            q,
            lang: lang === 'All' ? '' : lang,
            cursor: cursor || '',
            limit: 20,
          });
          return res.data;
        }}
        excludeSongIds={new Set((lineup?.items || []).map(s => s.song_id))}
        onAddSong={addSong}
        title="Add Song to Lineup"
      />

      {/* ── Fullscreen Viewer ── */}
      {viewerOpen && (
        <SongViewerModal
          songs={lineupSongs}
          contents={contents}
          loadingContent={loadingContent}
          initialIndex={viewerIndex}
          onClose={() => setViewerOpen(false)}
          onFetchContent={fetchContent}
        />
      )}

      {/* ── Share Dialog ── */}
      <Dialog open={shareOpen} onOpenChange={shareModal.onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] flex flex-col" data-testid="lineup-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Share Lineup
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col gap-3 py-2 min-h-0">
            <Input
              data-testid="share-member-search"
              placeholder="Search members…"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
            />
            {loadingMembers ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-1 min-h-0" data-testid="share-members-list">
                {members.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No members found</p>
                ) : members.map(m => {
                  const uid = m.user_id;
                  const selected = selectedShareIds.includes(uid);
                  return (
                    <button
                      key={uid || m.email}
                      data-testid={`share-member-${uid}`}
                      onClick={() => {
                        if (!uid) return;
                        setSelectedShareIds(prev =>
                          selected ? prev.filter(id => id !== uid) : [...prev, uid]
                        );
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        selected ? 'bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-600' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 flex items-center justify-center text-sm font-semibold shrink-0">
                        {(m.name || m.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{m.name || m.email}</p>
                        {m.name && <p className="text-xs text-slate-400 truncate">{m.email}</p>}
                      </div>
                      {selected && <Check className="w-4 h-4 text-green-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => shareModal.close()}>Cancel</Button>
            <Button
              data-testid="lineup-share-confirm-btn"
              onClick={handleShare}
              disabled={sharing}
              className="bg-green-700 hover:bg-green-800 text-white"
            >
              {sharing ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Promote Dialog ── */}
      <Dialog open={promoteOpen} onOpenChange={promoteModal.onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] flex flex-col" data-testid="lineup-promote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-4 h-4" /> Promote to Service
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col min-h-0 py-2">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 shrink-0">
              Select a service to replace its setlist with this lineup. The lineup will be deleted afterward.
            </p>
            {/* Search input */}
            <div className="relative mb-3 shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="promote-service-search"
                type="text"
                placeholder="Search services…"
                value={serviceSearch}
                onChange={e => setServiceSearch(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 outline-none focus:ring-1 focus:ring-green-500"
                style={{ fontSize: '16px' }}
              />
            </div>
            {loadingServices ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : eligibleServices.length === 0 ? (
              <div className="flex flex-col items-center py-6 text-center">
                <Users className="w-8 h-8 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">No upcoming services you can edit</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-1 min-h-0" data-testid="promote-services-list">
                {eligibleServices.map(svc => {
                  const label = svc.name ? `${svc.service_type} (${svc.name})` : svc.service_type;
                  return (
                    <button
                      key={svc.service_id}
                      data-testid={`promote-service-${svc.service_id}`}
                      onClick={() => setSelectedServiceId(svc.service_id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                        selectedServiceId === svc.service_id
                          ? 'bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-600'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="w-2 h-2 rounded-full bg-green-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{label}</p>
                        <p className="text-xs text-slate-400">{formatServiceDate(svc.date)}{svc.time ? ` · ${svc.time}` : ''}</p>
                      </div>
                      {selectedServiceId === svc.service_id && <Check className="w-4 h-4 text-green-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => promoteModal.close()}>Cancel</Button>
            <Button
              data-testid="lineup-promote-confirm-btn"
              onClick={handlePromote}
              disabled={!selectedServiceId || promoting}
              className="bg-green-700 hover:bg-green-800 text-white gap-1.5"
            >
              {promoting ? 'Promoting…' : <><Rocket className="w-3.5 h-3.5" /> Promote</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
